// Smart Sash Admin Panel - Certification Store
// Aligned with Swagger API specification
import {
  Certification,
  CertificationDTO,
  CertificationQuestion,
  CreateCertificationQuestionRequest,
  UpdateCertificationQuestionRequest,
} from '@/models';
import { certificationService } from '@/services/api/certificationService';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface CertificationState {
  certifications: CertificationDTO[];
  selectedCertification: CertificationDTO | null;
  questions: CertificationQuestion[];
  isLoading: boolean;
  error: string | null;

  // Certification methods (Swagger-compliant)
  fetchCertifications: (params?: { institutionId?: string; userId?: string }) => Promise<void>;
  fetchCertificationById: (id: string) => Promise<void>;
  createCertification: (data: Certification) => Promise<CertificationDTO>;
  deleteCertification: (id: string) => Promise<void>;
  setSelectedCertification: (certification: CertificationDTO | null) => void;

  // Questions methods (Swagger-compliant)
  fetchQuestions: (institutionId?: string) => Promise<void>;
  createQuestion: (data: CreateCertificationQuestionRequest) => Promise<CertificationQuestion>;
  updateQuestion: (
    id: string,
    data: UpdateCertificationQuestionRequest
  ) => Promise<CertificationQuestion>;
  deleteQuestion: (id: string) => Promise<void>;

  clearError: () => void;
}

export const useCertificationStore = create<CertificationState>()(
  devtools(
    (set) => ({
      certifications: [],
      selectedCertification: null,
      questions: [],
      isLoading: false,
      error: null,

      /**
       * Fetch all certifications
       * GET /api/Certification
       */
      fetchCertifications: async (params?: { institutionId?: string; userId?: string }) => {
        set({ isLoading: true, error: null });
        try {
          const certifications = await certificationService.getAll(params);
          set({ certifications, isLoading: false });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to fetch certifications'),
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Fetch certification by ID
       * GET /api/Certification/{id}
       */
      fetchCertificationById: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          const cert = await certificationService.getById(id);
          set({ selectedCertification: cert, isLoading: false });
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to fetch certification'), isLoading: false });
          throw error;
        }
      },

      /**
       * Create a new certification
       * POST /api/Certification
       */
      createCertification: async (data: Certification) => {
        set({ isLoading: true, error: null });
        try {
          const newCertification = await certificationService.create(data);
          set((state) => ({
            certifications: [...state.certifications, newCertification],
            isLoading: false,
          }));
          return newCertification;
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to create certification'),
            isLoading: false,
          });
          throw error;
        }
      },

      /**
       * Delete a certification
       * DELETE /api/Certification/{id}
       */
      deleteCertification: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          await certificationService.delete(id);
          set((state) => ({
            certifications: state.certifications.filter((c) => c.id !== id),
            selectedCertification:
              state.selectedCertification?.id === id ? null : state.selectedCertification,
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to delete certification'),
            isLoading: false,
          });
          throw error;
        }
      },

      setSelectedCertification: (certification: CertificationDTO | null) => {
        set({ selectedCertification: certification });
      },

      /**
       * Fetch all certification questions
       * GET /api/Certification/questions
       */
      fetchQuestions: async (institutionId?: string) => {
        set({ isLoading: true, error: null });
        try {
          const questions = await certificationService.getQuestions(institutionId);
          set({ questions, isLoading: false });
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to fetch questions'), isLoading: false });
          throw error;
        }
      },

      /**
       * Create a new certification question
       * POST /api/Certification/questions
       */
      createQuestion: async (data: CreateCertificationQuestionRequest) => {
        set({ isLoading: true, error: null });
        try {
          const newQuestion = await certificationService.createQuestion(data);
          set((state) => ({
            questions: [...state.questions, newQuestion],
            isLoading: false,
          }));
          return newQuestion;
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to create question'), isLoading: false });
          throw error;
        }
      },

      /**
       * Update an existing certification question
       * PUT /api/Certification/questions/{id}
       */
      updateQuestion: async (id: string, data: UpdateCertificationQuestionRequest) => {
        set({ isLoading: true, error: null });
        try {
          const updatedQuestion = await certificationService.updateQuestion(id, data);
          set((state) => ({
            questions: state.questions.map((q) => (q.id === id ? updatedQuestion : q)),
            isLoading: false,
          }));
          return updatedQuestion;
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to update question'), isLoading: false });
          throw error;
        }
      },

      /**
       * Delete a certification question
       * DELETE /api/Certification/questions/{id}
       */
      deleteQuestion: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          await certificationService.deleteQuestion(id);
          set((state) => ({
            questions: state.questions.filter((q) => q.id !== id),
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({ error: getErrorMessage(error, 'Failed to delete question'), isLoading: false });
          throw error;
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: 'CertificationStore' }
  )
);
